import clsx from 'clsx'
import { CircleDashedIcon, LoaderCircle, XIcon } from 'lucide-react'
import React from 'react'

import { SolidAlertTriangleIcon } from 'slab/icons/SolidAlertTriangle'
import { SolidCircleCheckIcon } from 'slab/icons/SolidCircleCheck'
import { formatFileSize, getTimezoneDatetimeStrings } from 'utils'

interface FileProps extends React.HTMLAttributes<HTMLDivElement> {
  actions?: React.ReactNode;
  errorMessage?: React.ReactNode;
  name: string;
  size?: number | string;
  status?: 'pending' | 'uploading' | 'success' | 'error'
  uploadedAt?: Date;
}

function formatUploadedAt (uploadedAt: Date) {
  const { formattedDate, formattedTime } = getTimezoneDatetimeStrings({ date: uploadedAt })
  return `${formattedDate} ${formattedTime}`
}

function renderDetails ({ size, uploadedAt }: { size?: number | string; uploadedAt?: Date }) {
  const filesize = (!!size || size === 0) && (
    <span>
      <span className='sr-only'>File size: </span>
      {formatFileSize(size)}
    </span>
  )

  const date = uploadedAt && (
    <span>Uploaded {formatUploadedAt(uploadedAt)}</span>
  )

  const details = [filesize, date].filter(Boolean)

  if (details.length > 0) {
    return (
      <div className='body-sm text-gray-500'>
        {details.map((detail, index) => (
          <span
            key={index}
            className='after:content-["•"] after:text-gray-400 after:mx-2 last:after:content-[""] last:after:mx-0'
          >
            {detail}
          </span>
        ))}
      </div>
    )
  }
}

export function File ({ actions, errorMessage, name, size, status, uploadedAt, ...props }: FileProps) {
  function renderStatusIcon () {
    switch (status) {
      case 'pending':
        return (
          <div className='text-gray-400'>
            <CircleDashedIcon aria-hidden size={16} />
          </div>
        )
      case 'uploading':
        return (
          <div className='text-gray-500 animate-spin'>
            <LoaderCircle aria-hidden size={16} />
          </div>
        )
      case 'success':
        return (
          <div className='text-green-400 '>
            <SolidCircleCheckIcon aria-hidden size={16} />
          </div>
        )
      case 'error':
        return (
          <div className='text-red-500'>
            <XIcon aria-hidden size={16} />
          </div>
        )
      default:
        return null
    }
  }

  return (
    <li>
      <div
        {...props}
        aria-label={props['aria-label'] ?? 'File'}
        role={props.role ?? 'group'}
        className={clsx(
          'border rounded-lg p-3 flex items-center justify-between gap-3',
          {
            'border-red-200': status === 'error',
            'border-gray-200': status !== 'error',
          }
        )}
      >
        <div className='flex gap-2 min-w-0'>
          {status && (
            <div className='flex items-center justify-center w-5 h-5'>
              <span className='sr-only'>Status: {status}</span>
              {renderStatusIcon()}
            </div>
          )}
          <div className='flex flex-col gap-0.5 min-w-0'>
            <div className='body text-gray-800 truncate' title={name}>
              <span className='sr-only'>File name: </span>
              {name}
            </div>
            {renderDetails({ size, uploadedAt })}
          </div>
        </div>
        {actions && (
          <div className='flex items-center gap-2 shrink-0'>{actions}</div>
        )}
      </div>
      {errorMessage && (
        <div className='body-sm text-red-500 flex items-center gap-1 mt-1'>
          <div className='text-red-400'>
            <SolidAlertTriangleIcon size={16} />
          </div>
          <div className='body-sm-bold text-red-700'>{errorMessage}</div>
        </div>
      )}
    </li>
  )
}

File.displayName = 'FileList.File'
